import { ConsoleSqlOutlined, FilterOutlined, PlusCircleFilled } from '@ant-design/icons'
import { PageHeader } from '@ant-design/pro-layout'
import type { ViewFieldsFragment } from '@salescore/client-api'
import { CONSTANT } from '@salescore/client-base'
import { canViewQueryFilter, useMe } from '@salescore/client-common'
import {
  CORE_CONSTANT,
  type ViewConfigFilterNode,
  viewConfigFilterNodeSchema,
  type ViewConfigTreeNode,
} from '@salescore/core'
// eslint-disable-next-line sonarjs/deprecation
import { addModelIdProperty, useBooleanState } from '@salescore/frontend-common'
import { App, Button, Divider, type ModalFuncProps, Select, Space, Switch, Tabs, type TabsProps } from 'antd'
import { isNotNullish } from 'effect/Predicate'
import { t } from 'i18next'
import { type ReactElement, useEffect, useMemo, useState } from 'react'
import { useRecoilValue } from 'recoil'

import { partitionJoinFilterNodes } from '../../../../domain/viewConfig/partitionJoinFilterNodes'
import { convertToFilterNodeForForm } from '../../../../recoil/navigation/hooks'
import { additionalConfigAtom } from '../../../../recoil/records/atoms.ts'
import { changesAtom } from '../../../../recoil/recordsHistory'
import { useGetModel } from '../../../../recoil/view/selectors/connectionsSelector'
import { useEditNodeMutation, useSetFilterMutation, useViewConfigSheet } from '../../../../recoil/viewConfig'
import { flatNodes } from '../../../../state/nodeUtility'
import { buildViewDebugModalProperties } from '../ViewDebugModal.tsx'
import type { ViewConfigFilterNodeForForm } from './filter/common'
import { applyAdvancedSettingsFlag, ViewFilterTreeForm } from './filter/ViewFilterTreeForm'

export const buildViewQueryFiltersModalProperties = ({
  initialFilterTree,
  onAfterFinish,
  view,
  setHasChangeToViewSchema,
}: {
  initialFilterTree?: ViewConfigFilterNodeForForm
  onAfterFinish: () => void
  view: ViewFieldsFragment
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ModalFuncProps => ({
  icon: null,
  width: '90%',
  cancelText: t(`閉じる`),
  okButtonProps: { style: { display: 'none' } },
  style: {
    maxWidth: 1000,
  },
  closable: true,
  maskClosable: true,
  destroyOnClose: true,
  content: (
    <ModalContent
      initialFilterTree={initialFilterTree}
      onAfterFinish={onAfterFinish}
      view={view}
      setHasChangeToViewSchema={setHasChangeToViewSchema}
    />
  ),
  footer: null,
})

// モーダルコンテンツコンポーネント

const ModalContent = ({
  initialFilterTree,
  onAfterFinish,
  view,
  setHasChangeToViewSchema,
}: {
  initialFilterTree?: ViewConfigFilterNodeForForm
  onAfterFinish: () => void
  view: ViewFieldsFragment
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ReactElement => {
  const { tree } = useViewConfigSheet()
  const { joinFiltersNodes, availableJoinFiltersNodes } = useMemo(() => partitionJoinFilterNodes(tree), [tree])
  const changes = useRecoilValue(changesAtom)
  const { message } = App.useApp()

  const tabItems: TabsProps['items'] = [
    {
      key: 'FiltersSetting',
      label: t(`フィルタ`),
      children: (
        <FiltersSetting
          initialFilterTree={initialFilterTree}
          onAfterFinish={onAfterFinish}
          view={view}
          setHasChangeToViewSchema={setHasChangeToViewSchema}
        />
      ),
    },
    joinFiltersNodes.length > 0 || availableJoinFiltersNodes.length > 0
      ? {
          key: 'JoinFiltersSettingList',
          label: t(`ブロックフィルタ`),
          children: (
            <JoinFiltersSettingList
              onAfterFinish={onAfterFinish}
              joinFiltersNodes={joinFiltersNodes}
              availableJoinFiltersNodes={availableJoinFiltersNodes}
              setHasChangeToViewSchema={setHasChangeToViewSchema}
            />
          ),
        }
      : null,
  ].filter(isNotNullish)

  useEffect(() => {
    if (changes.length > 0) {
      message.warning(t('設定を変更すると変更情報が失われます。'))
    }
  }, [changes.length, message])

  return (
    <PageHeader
      title={
        <Space>
          <FilterOutlined />
          <span>{t(`絞り込み`)}</span>
        </Space>
      }
    >
      <Tabs items={tabItems} />
    </PageHeader>
  )
}

const FiltersSetting = ({
  onAfterFinish,
  initialFilterTree,
  view,
  setHasChangeToViewSchema,
}: {
  onAfterFinish: () => void
  initialFilterTree: ViewConfigFilterNodeForForm | undefined
  view: ViewFieldsFragment
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ReactElement => {
  const me = useMe()
  const { modal } = App.useApp()
  const config = useViewConfigSheet()
  const flattenNodes = useMemo(() => flatNodes(config.tree), [config.tree])
  const getModel = useGetModel()
  const filterTree: ViewConfigFilterNodeForForm | undefined =
    initialFilterTree ?? (config.filterTree === undefined ? undefined : convertToFilterNodeForForm(config.filterTree))
  const setFilter = useSetFilterMutation()
  const additionalConfig = useRecoilValue(additionalConfigAtom)

  // eslint-disable-next-line sonarjs/deprecation
  const isAdvancedModeState = useBooleanState(
    (filterTree?.children ?? []).length > 0 || filterTree?.logicalOperator === 'or',
  )
  const availableModels = flattenNodes
    .map((node) => {
      const model = getModel(node.modelName)
      if (model === undefined) {
        // eslint-disable-next-line array-callback-return
        return
      }

      return {
        node,
        model,
      }
    })
    .filter(isNotNullish)
  const { properties } = useMemo(() => {
    const properties = availableModels.flatMap(({ model, node }) =>
      model.properties.map((property) => ({
        node,
        model,
        property,
      })),
    )
    const [rootModel] = availableModels
    const propertiesAndRootId = addModelIdProperty(rootModel, properties)
    return {
      properties: propertiesAndRootId,
    }
  }, [availableModels])

  return (
    <div className="flex flex-col gap-4 pt-4">
      <div className="self-end">
        <div className="flex items-center gap-4">
          <div className="flex gap-2">
            <div>{t(`高度なフィルタ条件`)}</div>
            <Switch
              checked={isAdvancedModeState.isTrue}
              onChange={(value) => {
                if (value) {
                  isAdvancedModeState.setTrue()
                } else {
                  isAdvancedModeState.setFalse()
                }
              }}
            />
          </div>

          {canViewQueryFilter(me) && (
            <Button
              type="text"
              className={CONSTANT.colorClasses.ADMIN_TEXT_COLOR}
              icon={<ConsoleSqlOutlined />}
              onClick={() => {
                const m = modal.confirm(
                  buildViewDebugModalProperties({
                    content: filterTree,
                    // eslint-disable-next-line sonarjs/todo-tag
                    schema: viewConfigFilterNodeSchema, // TODO: 指定はしているものの、現状ワークしていない
                    onChange(filterTree) {
                      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
                      setFilter({ filterTree: filterTree as ViewConfigFilterNode })
                      onAfterFinish()
                    },
                    onAfterFinish: () => {
                      m.destroy()
                    },
                  }),
                )
              }}
            >
              {t(`スキーマ表示`)}
            </Button>
          )}
        </div>
      </div>

      <ViewFilterTreeForm
        filterTree={filterTree}
        propertiesWithNode={properties}
        isAdvancedMode={isAdvancedModeState.isTrue}
        onAfterFinish={(filterTree) => {
          const newFilterTree = applyAdvancedSettingsFlag(isAdvancedModeState.isTrue, filterTree)
          setFilter({ filterTree: newFilterTree })
          if (
            view.id.startsWith(
              CORE_CONSTANT.KPI_SHEET_DYNAMIC_VIEW_ID_PREFIX + CORE_CONSTANT.KPI_PREVIEW_DRILL_DOWN_VIEW_ID_PREFIX,
            ) ||
            additionalConfig.previousDimmensions === undefined
          ) {
            // KPI タブの一覧パネル、KPI プレビューのドリルダウン、あるいはシートタブの場合は、フィルターを変更したら「設定を保存」ボタンを表示する
            // （ダッシュボードからドリルダウンした場合は、フィルターを変更しても「設定を保存」ボタンを表示しない）
            setHasChangeToViewSchema(true)
          }

          // ここで保存しても最新のviewになっていないので、間接的に保存をキューする
          onAfterFinish()
        }}
      />
    </div>
  )
}

const JoinFiltersSettingList = ({
  onAfterFinish,
  joinFiltersNodes,
  availableJoinFiltersNodes,
  setHasChangeToViewSchema,
}: {
  onAfterFinish: () => void
  joinFiltersNodes: ViewConfigTreeNode[]
  availableJoinFiltersNodes: ViewConfigTreeNode[]
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ReactElement => {
  const [isAdding, setIsAdding] = useState(false)

  return (
    <div className="flex flex-col pt-6">
      {joinFiltersNodes.map((joinFilterNode, index) => (
        <div key={joinFilterNode.name}>
          <JoinFiltersSetting
            onAfterFinish={onAfterFinish}
            node={joinFilterNode}
            setHasChangeToViewSchema={setHasChangeToViewSchema}
          />
          {index !== joinFiltersNodes.length - 1 && <Divider />}
        </div>
      ))}
      {availableJoinFiltersNodes.length > 0 &&
        (isAdding ? (
          <>
            {joinFiltersNodes.length > 0 && <Divider />}
            <NewJoinFiltersSetting
              onAfterFinish={() => {
                setIsAdding(false)
                onAfterFinish()
              }}
              availableJoinFiltersNodes={availableJoinFiltersNodes}
              setHasChangeToViewSchema={setHasChangeToViewSchema}
            />
          </>
        ) : (
          <Button
            type="text"
            icon={<PlusCircleFilled />}
            color={'blue'}
            style={{
              color: CONSTANT.colors.primaryColor,
              alignSelf: 'center',
            }}
            onClick={() => {
              setIsAdding(true)
            }}
          >
            {t(`新規追加`)}
          </Button>
        ))}
    </div>
  )
}

const JoinFiltersSetting = ({
  onAfterFinish,
  node,
  setHasChangeToViewSchema,
}: {
  onAfterFinish: () => void
  node: ViewConfigTreeNode
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ReactElement => {
  const { editNode } = {
    editNode: useEditNodeMutation(),
  }
  const getModel = useGetModel()
  const label = node.ui?.label ?? getModel(node.modelName)?.label ?? node.modelName

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div>{t(`オブジェクトを選択`)}</div>
          <div className="rounded border border-solid border-gray-300 px-4 py-1">{label}</div>
        </div>
        <Button
          danger
          onClick={() => {
            editNode({
              node,
              f: (node) => {
                // eslint-disable-next-line no-param-reassign
                node.additionalJoinOn = undefined
              },
              onSetConfig: () => {
                setHasChangeToViewSchema(true)
              },
            })
          }}
        >
          削除
        </Button>
      </div>
      <JoinFiltersSettingBody
        onAfterFinish={onAfterFinish}
        node={node}
        setHasChangeToViewSchema={setHasChangeToViewSchema}
      />
    </div>
  )
}

const NewJoinFiltersSetting = ({
  onAfterFinish,
  availableJoinFiltersNodes,
  setHasChangeToViewSchema,
}: {
  onAfterFinish: () => void
  availableJoinFiltersNodes: ViewConfigTreeNode[]
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ReactElement => {
  const getModel = useGetModel()
  const getLabel = (node: ViewConfigTreeNode) => node.ui?.label ?? getModel(node.modelName)?.label ?? node.modelName

  const [currentNode, setCurrentNode] = useState(availableJoinFiltersNodes[0])
  const onChangeCurrentNode = (value: string) => {
    setCurrentNode(availableJoinFiltersNodes.find((node) => node.name === value))
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div>{t(`オブジェクトを選択`)}</div>
          <Select
            value={currentNode?.name}
            options={availableJoinFiltersNodes.map((node) => ({ value: node.name, label: getLabel(node) }))}
            onChange={onChangeCurrentNode}
          />
        </div>
        {currentNode !== undefined && (
          <Button danger onClick={onAfterFinish}>
            削除
          </Button>
        )}
      </div>
      {currentNode !== undefined && (
        <JoinFiltersSettingBody
          key={currentNode.name}
          onAfterFinish={onAfterFinish}
          node={currentNode}
          setHasChangeToViewSchema={setHasChangeToViewSchema}
        />
      )}
    </div>
  )
}

const JoinFiltersSettingBody = ({
  onAfterFinish,
  node,
  setHasChangeToViewSchema,
}: {
  onAfterFinish: () => void
  node: ViewConfigTreeNode
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}) => {
  const { editNode } = {
    editNode: useEditNodeMutation(),
  }
  const getModel = useGetModel()
  // eslint-disable-next-line sonarjs/deprecation
  const isAdvancedModeState = useBooleanState(
    (node.additionalJoinOn?.children ?? []).length > 0 || node.additionalJoinOn?.logicalOperator === 'or',
  )
  const model = getModel(node.modelName)
  const propertiesWithNode =
    model?.properties.map((property) => ({
      node,
      model,
      property,
    })) ?? []
  const propertiesAndModelIdWithNode = addModelIdProperty({ node, model }, propertiesWithNode)

  return (
    <div className="flex flex-col gap-4">
      <div className="self-end">
        <div className="flex gap-2">
          <div>{t(`高度なフィルタ条件`)}</div>
          <Switch
            checked={isAdvancedModeState.isTrue}
            onChange={(value) => {
              if (value) {
                isAdvancedModeState.setTrue()
              } else {
                isAdvancedModeState.setFalse()
              }
            }}
          />
        </div>
      </div>

      <ViewFilterTreeForm
        isAdvancedMode={isAdvancedModeState.isTrue}
        filterTree={node.additionalJoinOn}
        propertiesWithNode={propertiesAndModelIdWithNode}
        onAfterFinish={(filterTree) => {
          editNode({
            node,
            f: (node) => {
              // eslint-disable-next-line no-param-reassign
              node.additionalJoinOn = applyAdvancedSettingsFlag(isAdvancedModeState.isTrue, filterTree)
            },
            onSetConfig: () => {
              setHasChangeToViewSchema(true)
            },
          })
          onAfterFinish()
        }}
      />
    </div>
  )
}
